import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../config";
import {
  Vehicle,
  VehicleUpdateRequest,
  VehicleCreateRequest,
} from "../../../../api/src/types";
import { prepareHeaders } from "../utils";

interface BasePathParams {
  userId: string;
  docId: string;
}

// linter takes away this const

// eslint-disable-next-line
const vehiclesTagType = "vehicles" as const;

export const vehiclesApi = createApi({
  reducerPath: "vehiclesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: [vehiclesTagType],
  endpoints: (builder) => ({
    getVehicle: builder.query<Vehicle, BasePathParams & { id: string }>({
      query: ({ userId, docId, id }) => ({
        url: `users/${userId}/documents/${docId}/vehicles/${id}`,
        method: "GET",
      }),

      providesTags: (_, __, query) => [{ type: vehiclesTagType, id: query.id }],
    }),
    getVehiclesByUserId: builder.query<Vehicle[], BasePathParams>({
      query: ({ userId, docId }) => {
        return `users/${userId}/documents/${docId}/vehicles`;
      },
      merge: (currentCache, newItems) => {
        const itemMap = new Map(currentCache.map((item) => [item.id, item]));
        newItems.forEach((item) => itemMap.set(item.id, item));
        return Array.from(itemMap.values());
      },

      providesTags: (result) => [
        ...(result?.map((vehicle) => ({
          type: vehiclesTagType,
          id: vehicle.id,
        })) ?? []),
        { type: vehiclesTagType, id: "LIST" },
      ],
    }),

    updateVehicle: builder.mutation<
      Vehicle,
      BasePathParams & { id: string; data: VehicleUpdateRequest }
    >({
      query: ({ userId, docId, id, data }) => ({
        url: `users/${userId}/documents/${docId}/vehicles/${id}`,
        method: "PATCH",
        body: data,
      }),

      invalidatesTags: (_, __, { id }) => [{ type: vehiclesTagType, id }],
    }),

    createVehicle: builder.mutation<
      Vehicle,
      BasePathParams & { data: VehicleCreateRequest }
    >({
      query: ({ userId, docId, data }) => ({
        url: `users/${userId}/documents/${docId}/vehicles`,
        method: "POST",
        body: data,
      }),

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: newItem } = await queryFulfilled;
          if (newItem.userId && newItem.id && newItem.documentId) {
            dispatch(
              vehiclesApi.util.updateQueryData(
                "getVehiclesByUserId",
                { userId: newItem.userId, docId: newItem.documentId },
                (draft: Vehicle[]) => {
                  draft.push(newItem);
                },
              ),
            );
          }
        } catch (error) {
          console.log("rtk query createVehicle error", error);
        }
      },
    }),
    deleteVehicle: builder.mutation<Vehicle, BasePathParams & { id: string }>({
      query: ({ userId, docId, id }) => ({
        url: `users/${userId}/documents/${docId}/vehicles/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(
        { userId, docId, id },
        { dispatch, queryFulfilled },
      ) {
        await queryFulfilled;
        dispatch(
          vehiclesApi.util.updateQueryData(
            "getVehiclesByUserId",
            { userId, docId },
            (draft: Vehicle[]) => {
              return draft.filter((item) => item.id !== id);
            },
          ),
        );
      },
    }),
  }),
});

export const {
  useGetVehicleQuery,
  useGetVehiclesByUserIdQuery,
  useUpdateVehicleMutation,
  useCreateVehicleMutation,
  useDeleteVehicleMutation,
} = vehiclesApi;
